import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { Spinner } from "react-bootstrap";
import {
  IconClock,
  IconCalendar,
  IconMessageCircle,
  IconAlertCircle,
  IconRefresh,
  IconDeviceFloppy,
} from "@tabler/icons-react";
import Swal from "sweetalert2";

import TimeRangeMessageService, {
  DEFAULT_CONFIG,
  normalizeServerConfig,
  ensureSeconds,
  WEEKDAY_OPTIONS,
  MODE_OPTIONS,
  isCrossMidnight,
} from "../../../services/TimeRangeMessageServices";
import { useAgentHubContext } from "../contexts/AgentHubContext";

// =============================================== //
// STYLED COMPONENTS — base layout                 //
// =============================================== //

const Container = styled.div`
  padding: var(--spacing-lg, 24px);
  max-width: 920px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  background: white;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: var(--border-radius, 16px);
  padding: 20px 24px;
  box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.08));
  margin-bottom: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  svg { color: var(--primary, #2563eb); flex-shrink: 0; }
`;

const Title = styled.h3`
  margin: 0 0 4px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
`;

const Subtitle = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary, #6b7280);
  line-height: 1.4;
`;

const StatusBadge = styled.span`
  flex-shrink: 0;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(p) => (p.$active ? "#dcfce7" : "#f3f4f6")};
  color: ${(p) => (p.$active ? "#166534" : "#6b7280")};
`;

const CenteredSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 240px;
`;

// =============================================== //
// STYLED COMPONENTS — toggle + overlay            //
// =============================================== //

const ToggleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: var(--border-radius, 16px);
  padding: 18px 24px;
  box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.08));
  margin-bottom: 16px;
  gap: 16px;
`;

const ToggleLabel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  strong { color: var(--text-primary, #111827); font-size: 0.95rem; }
  small { color: var(--text-secondary, #6b7280); font-size: 0.8rem; }
`;

const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  flex-shrink: 0;
  input { opacity: 0; width: 0; height: 0; }
  span {
    position: absolute; cursor: pointer; inset: 0;
    background-color: ${(p) => (p.$on ? "#22c55e" : "#cbd5e1")};
    border-radius: 26px;
    transition: 0.2s;
    &::before {
      content: "";
      position: absolute; left: 3px; bottom: 3px;
      width: 20px; height: 20px;
      background: white;
      border-radius: 50%;
      transform: translateX(${(p) => (p.$on ? "24px" : "0")});
      transition: 0.2s;
    }
  }
`;

const DisabledOverlay = styled.div`
  position: relative;
  opacity: ${(p) => (p.$disabled ? 0.45 : 1)};
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};
  transition: opacity 0.2s;
`;

// =============================================== //
// STYLED COMPONENTS — form sections               //
// =============================================== //

const FormSection = styled.div`
  background: white;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: var(--border-radius, 16px);
  padding: 20px 24px;
  box-shadow: var(--shadow-sm, 0 2px 8px rgba(0, 0, 0, 0.08));
  margin-bottom: 16px;
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
  svg { color: var(--primary, #2563eb); }
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const TimeInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 140px;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-secondary, #6b7280);
`;

const TimeInput = styled.input`
  padding: 10px 12px;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 10px;
  font-size: 0.95rem;
  background: #f9fafb;
  &:focus {
    outline: none;
    border-color: var(--primary, #2563eb);
    background: white;
  }
`;

const ErrorText = styled.div`
  font-size: 0.75rem;
  color: #dc2626;
  margin-top: 4px;
`;

const CrossMidnightHint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 8px 12px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #92400e;
  svg { flex-shrink: 0; }
`;

const PlaygroundLimitationHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 14px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  font-size: 0.8rem;
  color: #1e40af;
  line-height: 1.4;
  svg { flex-shrink: 0; margin-top: 2px; }
  strong { font-weight: 600; }
`;

const WeekdayChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const WeekdayChip = styled.button`
  padding: 8px 14px;
  border-radius: 999px;
  border: 1.5px solid ${(p) => (p.$selected ? "var(--primary, #2563eb)" : "#e5e7eb")};
  background: ${(p) => (p.$selected ? "var(--primary, #2563eb)" : "white")};
  color: ${(p) => (p.$selected ? "white" : "#374151")};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { transform: translateY(-1px); }
`;

const ModeOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ModeCard = styled.button`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: ${(p) => (p.$selected ? "#eff6ff" : "white")};
  border: 1.5px solid ${(p) => (p.$selected ? "var(--primary, #2563eb)" : "#e5e7eb")};
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  &:hover { border-color: var(--primary, #2563eb); }
`;

const ModeRadio = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid ${(p) => (p.$selected ? "var(--primary, #2563eb)" : "#9ca3af")};
  background: ${(p) =>
    p.$selected
      ? "radial-gradient(circle, var(--primary, #2563eb) 40%, white 45%)"
      : "white"};
`;

const ModeText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ModeTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary, #111827);
`;

const ModeDescription = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary, #6b7280);
  line-height: 1.4;
`;

const MessageTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 14px;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 12px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  resize: vertical;
  background: #f9fafb;
  &:focus {
    outline: none;
    border-color: var(--primary, #2563eb);
    background: white;
  }
`;

const MessageHint = styled.div`
  margin-top: 6px;
  font-size: 0.75rem;
  color: var(--text-secondary, #6b7280);
  code {
    background: #f3f4f6;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.8em;
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 0.7rem;
  color: var(--text-secondary, #9ca3af);
  margin-top: 4px;
`;

// =============================================== //
// STYLED COMPONENTS — action bar                  //
// =============================================== //

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  padding: 16px 0 24px;
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { background: #f9fafb; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  border-radius: 10px;
  border: none;
  background: var(--gradient-primary, linear-gradient(135deg, #2563eb, #4f46e5));
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
  &:hover:not(:disabled) { transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

// =============================================== //
// COMPONENT                                       //
// =============================================== //

const TimeRangeMessage = () => {
  const ctx = useAgentHubContext();
  const agentId = ctx?.agentId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [originalConfig, setOriginalConfig] = useState(DEFAULT_CONFIG);
  const [errors, setErrors] = useState({});

  const hasChanges = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(originalConfig),
    [config, originalConfig]
  );

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await TimeRangeMessageService.getConfig(agentId);
        if (cancelled) return;
        const normalized = normalizeServerConfig(data);
        setConfig(normalized);
        setOriginalConfig(normalized);
      } catch (err) {
        if (cancelled) return;
        Swal.fire({
          icon: "error",
          title: "Erro ao carregar",
          text: "Não foi possível carregar a configuração de mensagens por horário.",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  const toggleWeekday = (value) => {
    setConfig((prev) => ({
      ...prev,
      active_weekdays: prev.active_weekdays.includes(value)
        ? prev.active_weekdays.filter((d) => d !== value)
        : [...prev.active_weekdays, value].sort((a, b) => a - b),
    }));
  };

  const validate = useCallback(() => {
    const errs = {};
    if (!config.enabled) return errs; // disabled é sempre válido
    if (!config.start_time) errs.start_time = "Obrigatório";
    if (!config.end_time) errs.end_time = "Obrigatório";
    if (
      config.start_time &&
      config.end_time &&
      config.start_time === config.end_time
    ) {
      errs.end_time = "Início e fim não podem ser iguais";
    }
    if (!config.active_weekdays?.length) {
      errs.active_weekdays = "Selecione ao menos um dia";
    }
    if (!config.message?.trim()) {
      errs.message = "Mensagem obrigatória quando habilitada";
    }
    return errs;
  }, [config]);

  const handleSave = useCallback(async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      Swal.fire({
        icon: "warning",
        title: "Verifique os campos",
        text: "Existem campos com erro. Corrija antes de salvar.",
      });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...config,
        start_time: ensureSeconds(config.start_time),
        end_time: ensureSeconds(config.end_time),
      };
      const saved = await TimeRangeMessageService.updateConfig(agentId, payload);
      const normalized = normalizeServerConfig(saved);
      setConfig(normalized);
      setOriginalConfig(normalized);
      setErrors({});
      Swal.fire({
        icon: "success",
        title: "Salvo!",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (typeof err?.response?.data === "string" ? err.response.data : null) ||
        "Tente novamente.";
      Swal.fire({ icon: "error", title: "Erro ao salvar", text: detail });
    } finally {
      setSaving(false);
    }
  }, [agentId, config, validate]);

  const handleReset = useCallback(() => {
    setConfig(originalConfig);
    setErrors({});
  }, [originalConfig]);

  if (loading) {
    return (
      <Container>
        <CenteredSpinner>
          <Spinner animation="border" variant="primary" />
        </CenteredSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <IconClock size={28} />
          <div>
            <Title>Mensagens por Horário</Title>
            <Subtitle>
              Envie uma mensagem automática quando o cliente entrar em contato
              em uma janela específica de horário.
            </Subtitle>
          </div>
        </HeaderLeft>
        <StatusBadge $active={config.enabled}>
          {config.enabled ? "Ativo" : "Inativo"}
        </StatusBadge>
      </Header>

      <ToggleCard>
        <ToggleLabel>
          <strong>Habilitar mensagens por horário</strong>
          <small>
            Quando ativo, mensagens recebidas dentro da janela disparam o envio
            determinístico configurado abaixo.
          </small>
        </ToggleLabel>
        <Switch $on={config.enabled}>
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
          />
          <span />
        </Switch>
      </ToggleCard>

      <DisabledOverlay $disabled={!config.enabled}>
        <FormSection>
          <SectionTitle>
            <IconClock size={18} /> Janela de horário
          </SectionTitle>
          <Row>
            <TimeInputGroup>
              <Label>De</Label>
              <TimeInput
                type="time"
                value={config.start_time}
                onChange={(e) => setConfig({ ...config, start_time: e.target.value })}
              />
              {errors.start_time && <ErrorText>{errors.start_time}</ErrorText>}
            </TimeInputGroup>
            <TimeInputGroup>
              <Label>Até</Label>
              <TimeInput
                type="time"
                value={config.end_time}
                onChange={(e) => setConfig({ ...config, end_time: e.target.value })}
              />
              {errors.end_time && <ErrorText>{errors.end_time}</ErrorText>}
            </TimeInputGroup>
          </Row>
          {isCrossMidnight(config.start_time, config.end_time) && (
            <CrossMidnightHint>
              <IconAlertCircle size={14} />
              Janela atravessa a meia-noite (ex: 22h–08h). Comportamento esperado.
            </CrossMidnightHint>
          )}
        </FormSection>

        <FormSection>
          <SectionTitle>
            <IconCalendar size={18} /> Dias ativos
          </SectionTitle>
          <WeekdayChips>
            {WEEKDAY_OPTIONS.map((day) => (
              <WeekdayChip
                key={day.value}
                type="button"
                title={day.fullLabel}
                $selected={config.active_weekdays.includes(day.value)}
                onClick={() => toggleWeekday(day.value)}
              >
                {day.label}
              </WeekdayChip>
            ))}
          </WeekdayChips>
          {errors.active_weekdays && <ErrorText>{errors.active_weekdays}</ErrorText>}
        </FormSection>

        <FormSection>
          <SectionTitle>Modo de envio</SectionTitle>
          <ModeOptions>
            {MODE_OPTIONS.map((opt) => (
              <ModeCard
                key={opt.value}
                type="button"
                $selected={config.mode === opt.value}
                onClick={() => setConfig({ ...config, mode: opt.value })}
              >
                <ModeRadio $selected={config.mode === opt.value} />
                <ModeText>
                  <ModeTitle>{opt.title}</ModeTitle>
                  <ModeDescription>{opt.description}</ModeDescription>
                </ModeText>
              </ModeCard>
            ))}
          </ModeOptions>
          {config.mode === 'send_and_deactivate' && (
            <PlaygroundLimitationHint>
              <IconAlertCircle size={16} />
              <div>
                <strong>Comportamento no Playground:</strong> a desativação real do bot
                não ocorre durante testes (proteção para não afetar atendimentos reais).
                A mensagem customizada e a interrupção da resposta da IA aparecem na
                primeira interação, mas em mensagens seguintes a custom message dispara
                novamente em vez de deixar o bot em silêncio. Para validar o comportamento
                completo de "bot desativado até o fim do range", teste no WhatsApp real.
              </div>
            </PlaygroundLimitationHint>
          )}
        </FormSection>

        <FormSection>
          <SectionTitle>
            <IconMessageCircle size={18} /> Mensagem
          </SectionTitle>
          <MessageTextarea
            value={config.message}
            onChange={(e) => setConfig({ ...config, message: e.target.value })}
            placeholder="Ex: Olá! Estamos fora do expediente. [[break]] Retornamos amanhã às 8h."
            maxLength={2000}
            rows={5}
          />
          <MessageHint>
            💡 Use <code>[[break]]</code> para dividir o texto em múltiplas mensagens enviadas em sequência.
          </MessageHint>
          {errors.message && <ErrorText>{errors.message}</ErrorText>}
          <CharCount>{config.message.length} / 2000</CharCount>
        </FormSection>
      </DisabledOverlay>
      <ActionBar>
        <SecondaryButton
          type="button"
          onClick={handleReset}
          disabled={!hasChanges || saving}
        >
          <IconRefresh size={16} />
          Cancelar alterações
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <IconDeviceFloppy size={16} />
          )}
          {saving ? "Salvando..." : "Salvar configuração"}
        </PrimaryButton>
      </ActionBar>
    </Container>
  );
};

export default TimeRangeMessage;
